import { cn } from "@base/lib/utils";
import { useIntersectionObserver } from "@base/model/hooks";
import { imageCache, useImageSource } from "@base/model/hooks/useImageCache";
import * as Sentry from "@sentry/react";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ImageSource {
  srcSet: string;
  type: string;
  sizes?: string;
}

export interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt"> {
  src: string;
  /** 이미지의 대체 텍스트 (필수). 장식용 이미지는 빈 문자열("")을 명시적으로 전달 */
  alt: string;
  /** true 면 HEIC 변환 경로를 강제한다. blob URL 처럼 확장자가 없는 HEIC 소스에 필요. 미지정 시 확장자로 추정. */
  isHeic?: boolean;
  /** true일 때 뷰포트 진입 시점까지 이미지 로드를 지연 */
  lazy?: boolean;
  /** avif, webp 등 여러 포맷을 제공하여 브라우저가 최적 포맷 선택 가능 */
  sources?: ImageSource[];
  /** 원본 이미지 로드 실패 시 표시할 대체 이미지 경로 */
  fallbackSrc?: string;
  /** CLS 방지를 위한 종횡비 (e.g. "16/9", "4/3", "1/1") */
  aspectRatio?: string;
  /** 캐시 레이어(변환/디코드) 실패 시 호출 — src 가 null 이 되어 DOM onerror 가 발화하지 않는 경우에도 통보 */
  onCacheError?: () => void;
  /** true면 캐시 레이어 에러를 Sentry에 전송한다. 사용자 로컬 파일 미리보기처럼 예상 가능한 실패는 끌 수 있다. */
  reportCacheError?: boolean;
}

/**
 * cold path 에서 skeleton → 이미지 사이의 자연스러운 페이드인 길이 (ms).
 *
 * 캐시 hit hot path 는 페이드를 아예 건너뛰고 즉시 표시하므로 (아래 isDecoded 초기값 참고)
 * 이 값은 "첫 디코드를 거쳐야 하는 cold path" 에서만 의미가 있다.
 */
const DECODE_FADE_MS = 120;

/**
 * Image
 *
 * - 모든 이미지 소스는 `useImageSource` 를 통해 단일 캐시(`ImageCache`) 를 거친다.
 *   캐시는 (1) HEIC → JPEG 변환, (2) off-DOM `<img>` 사전 디코드, (3) LRU 메모리 상한을 담당한다.
 * - 가시 `<img>` 는 src 가 결정된 뒤 `decode()` 완료를 기다린 후에야 opacity 로 페이드인한다.
 *   덕분에 가상화 환경에서 셀이 unmount → remount 되어도 흰 프레임이 노출되지 않는다.
 * - sources / fallbackSrc / aspectRatio / lazy / forwardRef 같은 표준 표시 옵션은 이전과 동일.
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      src,
      alt,
      isHeic,
      lazy = false,
      sources,
      className,
      loading,
      onLoad,
      onError,
      onCacheError,
      fallbackSrc,
      aspectRatio,
      reportCacheError = true,
      style,
      ...rest
    },
    forwardedRef,
  ) => {
    const [hasError, setHasError] = useState(false);
    const [hasIntersected, setHasIntersected] = useState(false);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const onCacheErrorRef = useRef(onCacheError);
    onCacheErrorRef.current = onCacheError;

    const observerOptions = useMemo(() => ({ rootMargin: "50px" }), []);
    const { ref: observerRef, isIntersecting } = useIntersectionObserver(observerOptions);

    if (isIntersecting && !hasIntersected) {
      setHasIntersected(true);
    }

    // lazy 모드에서는 viewport 진입 전까지 캐시도 acquire 하지 않는다.
    // 가상화 환경에서는 셀 자체가 viewport 기준으로 mount/unmount 되므로 lazy 미지정이 자연스럽다.
    const shouldLoad = !lazy || hasIntersected;
    const { src: resolvedSrc, error } = useImageSource(src, { enabled: shouldLoad, isHeic });

    // 캐시 layer 가 변환/디코드 실패를 보고했거나 (= `error`), 실제 `<img>` 가 onError 를 발생시킨
    // 경우 (= `hasError`) 모두 동일하게 fallbackSrc 로 전환한다.
    //
    // 과거에는 cache error 시 원본 `src` 로 낙착하는 graceful degradation 을 시도했는데,
    // HEIC 처럼 브라우저가 native 표시 못 하는 포맷에서는 오히려 깨진 이미지 + DOM 에 .heic 주소가
    // 그대로 남는 사고로 이어졌다. 캐시는 컴포넌트 re-mount 시 자동 retry 하므로 (acquire 재시도 정책)
    // 여기서는 안전하게 fallback / skeleton 만 노출하고 실제 복구는 캐시에 위임한다.
    const loadFailed = hasError || !!error;
    const displaySrc = loadFailed && fallbackSrc ? fallbackSrc : resolvedSrc;

    /**
     * 첫 mount 시점에 캐시가 이미 ready URL 을 동기로 내려줬다면 (peek hit),
     * 비트맵은 브라우저 메모리에 hot 한 상태이므로 페이드인 없이 즉시 표시해도 흰 프레임이 없다.
     *
     * 이 lazy initializer 가 cache hit 시 한 프레임의 opacity:0 + 120ms 페이드 비용을 통째로 제거한다.
     */
    const [isDecoded, setIsDecoded] = useState(() => !!resolvedSrc);

    /**
     * "이 src 로는 이미 디코드 완료를 통보한 적이 있다" 를 기록.
     *
     * 첫 mount 가 캐시 hit 이라면 그 src 를 미리 마킹해 두어, 이어서 실행되는
     * decode effect 가 같은 src 에 대해 isDecoded 를 false 로 되돌리고 페이드를 강제하는 것을 막는다.
     * (StrictMode 의 effect 재실행에서도 동일하게 페이드 재발화를 차단)
     */
    const decodedSrcRef = useRef<string | null>(resolvedSrc ?? null);

    /**
     * 같은 Image 인스턴스가 재사용되면서 `src` 만 바뀌는 케이스 (가상화/리스트/스왑 UI 등) 동기 처리.
     *
     * 이 derived state 가 없으면 다음 시퀀스가 발생해 cache hit 인데도 한 프레임 깜빡인다:
     *   1. src 변경 → useImageSource 가 render 중 setState 로 새 snapshot 으로 전환
     *      (그러나 이번 render 에서는 stale 한 옛 resolvedSrc 가 보임)
     *   2. 다음 render: 새 resolvedSrc 가 보이지만 isDecoded=true / decodedSrcRef=옛URL 그대로
     *      → 화면엔 새 이미지가 일단 노출됨
     *   3. decode effect 가 displaySrc !== decodedSrcRef 를 감지 → setIsDecoded(false)
     *      → opacity:0 으로 새 이미지가 잠깐 사라졌다가 페이드인 (= "flash of content")
     *
     * 핵심: 이번 render 의 `resolvedSrc` 는 stale 일 수 있으므로 직접 캐시를 peek 해
     * 새 src 의 ready 여부를 동기로 확정한다.
     */
    const [prevSrc, setPrevSrc] = useState(src);
    if (src !== prevSrc) {
      setPrevSrc(src);
      const peeked = imageCache.peek(src, { isHeic });
      const peekedReadyUrl = peeked?.status === "ready" ? (peeked.url ?? null) : null;
      setIsDecoded(!!peekedReadyUrl);
      decodedSrcRef.current = peekedReadyUrl;
    }

    useEffect(() => {
      if (!reportCacheError) return;
      if (!error) return;
      Sentry.captureException(error, {
        tags: { type: "image_load_error" },
        contexts: { image: { src } },
      });
    }, [error, reportCacheError, src]);

    useEffect(() => {
      if (!error) return;
      onCacheErrorRef.current?.();
    }, [error]);

    // displaySrc 가 실제로 바뀌었을 때만 decode 사이클을 재시작한다.
    // hot path (peek hit) 에서는 displaySrc === decodedSrcRef.current 이므로 이 effect 는 no-op.
    useEffect(() => {
      if (!displaySrc) {
        decodedSrcRef.current = null;
        setIsDecoded(false);
        return;
      }

      if (decodedSrcRef.current === displaySrc) return;

      setIsDecoded(false);

      const img = imgRef.current;
      if (!img) return;

      let cancelled = false;
      const markReady = () => {
        if (cancelled) return;
        decodedSrcRef.current = displaySrc;
        setIsDecoded(true);
      };

      if (typeof img.decode === "function") {
        img.decode().then(markReady, markReady);
      } else if (img.complete) {
        markReady();
      } else {
        img.addEventListener("load", markReady, { once: true });
        img.addEventListener("error", markReady, { once: true });
      }

      return () => {
        cancelled = true;
      };
    }, [displaySrc]);

    const mergedRef = useCallback(
      (node: HTMLImageElement | null) => {
        imgRef.current = node;
        if (lazy) observerRef(node);
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [lazy, observerRef, forwardedRef],
    );

    const hasSrc = !!displaySrc;
    const showBitmap = hasSrc && isDecoded;
    // 표시할 비트맵이 없는 모든 상태 (변환 대기 / 변환 중 / URL 결정 후 디코드 대기) 에서 skeleton 노출.
    const isPlaceholder = !showBitmap;

    /**
     * opacity:0 은 `<img>` 에 src 가 이미 붙어 broken/undecoded 비트맵이 한 프레임 깜빡일 수 있는
     * 상태에서만 적용한다. src 자체가 없는 (HEIC 변환 대기 등) 단계에서 opacity:0 을 주면
     * skeleton-shimmer 배경까지 같이 투명해져 흰 화면이 노출되는 사고가 생긴다.
     */
    const mergedStyle: React.CSSProperties = {
      ...(aspectRatio ? { aspectRatio } : null),
      ...style,
      opacity: hasSrc && !isDecoded ? 0 : undefined,
      transition: `opacity ${DECODE_FADE_MS}ms ease-out`,
    };

    const handleLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        onLoad?.(e);
      },
      [onLoad],
    );

    const handleError = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (!hasError && fallbackSrc) {
          setHasError(true);
        }
        onError?.(e);
      },
      [hasError, fallbackSrc, onError],
    );

    const isDecorative = alt === "";
    const loadingStrategy = loading ?? (lazy ? "lazy" : "eager");

    const imageElement = (
      <img
        ref={mergedRef}
        src={displaySrc ?? undefined}
        alt={alt}
        role={isDecorative ? "presentation" : undefined}
        aria-hidden={isDecorative || undefined}
        className={cn(
          lazy && "lazy-loading-image",
          showBitmap && "loaded",
          isPlaceholder && "skeleton-shimmer",
          className,
        )}
        loading={shouldLoad ? loadingStrategy : undefined}
        decoding="async"
        style={mergedStyle}
        onLoad={handleLoad}
        onError={handleError}
        {...rest}
      />
    );

    if (sources?.length) {
      return (
        <picture>
          {shouldLoad &&
            !hasError &&
            sources.map(({ srcSet, type, sizes }) => <source key={srcSet} srcSet={srcSet} type={type} sizes={sizes} />)}
          {imageElement}
        </picture>
      );
    }

    return imageElement;
  },
);

Image.displayName = "Image";
